import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { analyticsApi } from '../services/analytics-api.service';
import type {
  CoverageQueryParams,
  CoverageDrillDownParams,
} from '../types/analytics.types';

export const coverageKeys = {
  all: ['analytics', 'coverage'] as const,
  list: (params?: CoverageQueryParams) =>
    [...coverageKeys.all, 'list', params] as const,
  drillDown: (params: Omit<CoverageDrillDownParams, 'cursor'>) =>
    [...coverageKeys.all, 'drill-down', params] as const,
};

export const useCoverageData = (params?: CoverageQueryParams, enabled = true) => {
  return useQuery({
    queryKey: coverageKeys.list(params),
    queryFn: async () => {
      const response = await analyticsApi.getCoverage(params);
      return response.data;
    },
    staleTime: 30 * 60 * 1000,
    enabled,
  });
};

export const useCoverageDrillDown = (
  params: Omit<CoverageDrillDownParams, 'cursor'>,
  enabled = false,
) => {
  const { stateCode, ...rest } = params;

  return useInfiniteQuery({
    queryKey: coverageKeys.drillDown(params),
    queryFn: async ({ pageParam }) => {
      const response = await analyticsApi.getCoverageDrillDown({
        stateCode,
        ...rest,
        cursor: pageParam ?? undefined,
      });
      return response.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: enabled && !!stateCode,
    staleTime: 30 * 60 * 1000,
  });
};
